/**
 * assets/dm-rail.js — the horizontal-scroll-rail's behaviour layer.
 *
 * `snippets/dm-rail.liquid` renders the shell (track + two arrow buttons)
 * and the scroll-snap is pure CSS. This file adds exactly the two things CSS
 * cannot: clicking an arrow scrolls the track by ~80% of its own visible
 * width (`scrollBand`, transcribed verbatim from 01-pdp-desktop.md §14's
 * "Rail JS" block), and each arrow's `disabled` state tracks whether there
 * is anywhere left to scroll in that direction.
 *
 * JS OWNS ONLY THE ARROWS. The track itself is never scrolled, snapped or
 * measured for any purpose beyond that — no keyboard re-implementation, no
 * focus trap. Every item inside the track is independently focusable, and a
 * browser natively scrolls an `overflow:auto` ancestor to reveal a focused
 * descendant, so Tab already reaches every item regardless of arrow state.
 *
 * Classic script, ES2020, built on `DMElement` (dm-core.js) — every listener
 * goes through `this.on()` so a theme-editor subtree swap (disconnect then
 * reconnect) cannot leak a second copy.
 */
(function () {
  'use strict';

  class DMRail extends DMElement {
    init() {
      // NOT `this.track`: that name is DMElement's `track(observer)` method, and an
      // own property shadows it, so `observeResize()` threw and init() never
      // reached `updateButtons()` — arrows started in the wrong enabled state.
      this.scroller = this.querySelector(':scope > .dm-rail__track');
      if (!this.scroller) return;

      this.prevBtn = this.querySelector('[data-dm-rail-prev]');
      this.nextBtn = this.querySelector('[data-dm-rail-next]');

      if (this.prevBtn) this.on(this.prevBtn, 'click', () => this.scrollBand(-1));
      if (this.nextBtn) this.on(this.nextBtn, 'click', () => this.scrollBand(1));

      // Coalesced through `this.frame()` (dm-core.js) so a scroll-linked
      // read/write never runs more than once per animation frame.
      this.on(this.scroller, 'scroll', () => this.frame(() => this.updateButtons()), { passive: true });
      this.observeResize(() => this.frame(() => this.updateButtons()));

      this.updateButtons();
    }

    /** Scroll by ~80% of the track's own visible width — the exact ratio
     *  01-pdp-desktop.md §14 documents for the UGC rail's arrows. The
     *  fallback below covers a browser/context where smooth-scroll silently
     *  no-ops (same shape as the design doc's own `scrollBand`). */
    scrollBand(dir) {
      if (!this.scroller) return;
      const delta = dir * this.scroller.clientWidth * 0.8;
      const from = this.scroller.scrollLeft;
      this.scroller.scrollBy({ left: delta, behavior: this.scrollBehavior });
      setTimeout(() => {
        if (this.scroller.scrollLeft === from) this.scroller.scrollLeft = from + delta;
      }, 120);
    }

    updateButtons() {
      if (!this.scroller) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      const overflowing = scrollWidth > clientWidth + 1;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      if (this.prevBtn) {
        this.attr(this.prevBtn, 'disabled', !overflowing || atStart);
        this.attr(this.prevBtn, 'aria-disabled', !overflowing || atStart ? 'true' : null);
      }
      if (this.nextBtn) {
        this.attr(this.nextBtn, 'disabled', !overflowing || atEnd);
        this.attr(this.nextBtn, 'aria-disabled', !overflowing || atEnd ? 'true' : null);
      }
    }
  }

  DMElement.define('dm-rail', DMRail);
})();
